// ================================================================
// src/pages/admin/AdminBooks.jsx
// ================================================================
// داشبورد مدیر — مرکز کنترل درخواست‌ها و پروژه‌ها
// ----------------------------------------------------------------
// مسئولیت‌ها:
//   ۱. Header با آمار کلی سیستم + NotificationBell
//   ۲. Tab: درخواست‌های جدید (Pending Requests)
//   ۳. Tab: بروزرسانی‌ها (Update Pending)
//   ۴. Tab: همه‌ی پروژه‌ها (با فیلتر و جستجو)
//   ۵. Tab: Audit Log
//   ۶. کارت درخواست با تمام جزئیات معلم + پروژه‌های قبلی
//   ۷. کارت بروزرسانی با diff خلاصه + impact preview
//   ۸. Approve/Reject/Request-Edit با دلیل
//   ۹. Admin Edit Request Modal
//  ۱۰. ورود به صفحه‌ی Review (فایل ۹) برای بررسی عمیق
// ================================================================

import React, {
  useState, useEffect, useMemo, useCallback, useRef,
} from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.jsx';
import './admin-books.css';
import '../../components/books/books-shared.css';

import {
  BooksEngineProvider,
  useBooksEngine, useEngineReady,
  useSystemStats, usePendingRequests, useAllProjects,
  useTeacherProfile, useProject, useProjectVersions, useVersion,
  useAdminRequestActions, useProjectActions,
  useDiff, useImpactAnalysis, useAuditLog,
  useKeyboardShortcuts,
  REQUEST_STATUS, PROJECT_STATE, VERSION_STATUS,
  NOTIF_TYPE, AUDIT_ACTION,
  GRADE_TITLES, SUBJECT_LABELS,
  normalizeGrade, normalizeSubject,
} from '../../hooks/useBooksEngine.js';

// 🔽 CHANGED: curriculum helpers برای فیلترهای ۱-۱۲ و رشته
import {
  GRADE_LIST,
  FIELD_LIST,
  getGrade as getGradeMeta,
  getField as getFieldMeta,
  needsField as curriculumNeedsField,
  normalizeFieldId as curriculumNormalizeField,
  normalizeGradeId as curriculumNormalizeGrade,
} from '../../core/education/curriculum.js';

import dataEngine from '../../core/books/data.js';
import { engine } from '../../core/books/storage.js';
import { diffEngine, auditLog as auditLogStore } from '../../core/books/logic.js';

import {
  Spinner, ProgressBar, Badge, Chip, EmptyState, SkeletonLoader,
  Modal, ConfirmModal,
  BlobImage, NotificationBell, VersionBadge, ProjectStateBadge,
  DiffViewer, ImpactPanel,
} from '../../components/books/BooksSharedUI.jsx';

// ================================================================
// SECTION 1: ICONS
// ================================================================

const IconSearch = (p) => (
  <svg viewBox="0 0 24 24" width={p.size || 18} height={p.size || 18} fill="none" {...p}>
    <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.8"/>
    <path d="M21 21l-4.3-4.3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);
const IconRefresh = (p) => (
  <svg viewBox="0 0 24 24" width={p.size || 18} height={p.size || 18} fill="none" {...p}>
    <path d="M23 4v6h-6M1 20v-6h6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const IconEye = (p) => (
  <svg viewBox="0 0 24 24" width={p.size || 16} height={p.size || 16} fill="none" {...p}>
    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" stroke="currentColor" strokeWidth="1.7"/>
    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.7"/>
  </svg>
);
const IconCheck = (p) => (
  <svg viewBox="0 0 24 24" width={p.size || 16} height={p.size || 16} fill="none" {...p}>
    <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const IconX = (p) => (
  <svg viewBox="0 0 24 24" width={p.size || 16} height={p.size || 16} fill="none" {...p}>
    <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);
const IconEdit = (p) => (
  <svg viewBox="0 0 24 24" width={p.size || 16} height={p.size || 16} fill="none" {...p}>
    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const IconArrow = (p) => (
  <svg viewBox="0 0 24 24" width={p.size || 18} height={p.size || 18} fill="none" {...p}>
    <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const IconClock = (p) => (
  <svg viewBox="0 0 24 24" width={p.size || 16} height={p.size || 16} fill="none" {...p}>
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.7"/>
    <path d="M12 7v5l3 2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/>
  </svg>
);








// 🔽 CHANGED: helper برای نمایش «پایه دهم (ریاضی)» — فقط اگه field داشته باشه
const formatGradeField = (grade, field) => {
  if (!grade) return '';
  const gradeTitle = GRADE_TITLES[String(grade)] || `پایه ${grade}`;
  if (!field) return gradeTitle;
  const f = getFieldMeta(field);
  return f ? `${gradeTitle} (${f.shortTitle})` : gradeTitle;
};

// ================================================================
// SECTION 2: CONSTANTS
// ================================================================

const TABS = Object.freeze({
  REQUESTS: 'requests',
  UPDATES: 'updates',
  PROJECTS: 'projects',
  AUDIT: 'audit',
});

const TAB_LABELS = {
  [TABS.REQUESTS]: 'درخواست‌های جدید',
  [TABS.UPDATES]: 'در انتظار بررسی',   // 🆕 (شامل اولیه + بروزرسانی)
  [TABS.PROJECTS]: 'همه‌ی پروژه‌ها',
  [TABS.AUDIT]: 'تاریخچه‌ی اقدامات',
};

// ================================================================
// SECTION 3: MAIN ENTRY
// ================================================================

export default function AdminBooksPage() {
  const { user } = useAuth();

  if (!user || user.role !== 'admin') {
    return <AdminAccessDenied user={user} />;
  }

  return (
    <BooksEngineProvider user={user}>
      <AdminBooksInner />
    </BooksEngineProvider>
  );
}

function AdminAccessDenied({ user }) {
  const navigate = useNavigate();
  const target = { student: '/StudentHome', teacher: '/TeacherHome', parent: '/ParentHome' }[user?.role] || '/login';
  return (
    <div className="ab-denied">
      <div className="ab-denied-card">
        <div className="ab-denied-icon">🔐</div>
        <h2>دسترسی محدود</h2>
        <p>این صفحه مخصوص مدیر سیستم است.</p>
        <button className="ab-btn ab-btn-primary" onClick={() => navigate(target)}>بازگشت</button>
      </div>
    </div>
  );
}

// ================================================================
// SECTION 4: INNER
// ================================================================

function AdminBooksInner() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, toast } = useBooksEngine();
  const { ready, initError } = useEngineReady();

  // --- URL-based tab ---
  const initialTab = useMemo(() => {
    const p = new URLSearchParams(location.search).get('tab');
    return Object.values(TABS).includes(p) ? p : TABS.REQUESTS;
  }, [location.search]);
  const [activeTab, setActiveTab] = useState(initialTab);

    // 🌗 state تم تیره برای این صفحه
// 🌗 تم — وصل شده به ناوبار بالا (navbar2-drawer-dark)
// 🆕 خواندن از engine (هماهنگ با remoteSync)
const [isDarkMode, setIsDarkMode] = useState(() => {
  try {
    return engine.getRaw('navbar2-drawer-dark') === '1';
  } catch {
    return false;
  }
});

const toggleTheme = useCallback(() => setIsDarkMode((prev) => !prev), []);

// ذخیره در کلید مشترک
// 🆕 از engine → remoteSync → userPrefs (سرور)
useEffect(() => {
  try {
    engine.setRaw('navbar2-drawer-dark', isDarkMode ? '1' : '0');
  } catch {}
}, [isDarkMode]);

// گوش دادن به تغییرات ناوبر
useEffect(() => {
  const handleStorage = (e) => {
    if (e.key === 'navbar2-drawer-dark') {
      setIsDarkMode(e.newValue === '1');
    }
  };
  window.addEventListener('storage', handleStorage);

  const interval = setInterval(() => {
    try {
      // 🆕 از engine
      const current = engine.getRaw('navbar2-drawer-dark') === '1';
      setIsDarkMode((prev) => (prev !== current ? current : prev));
    } catch {}
  }, 500);

  return () => {
    window.removeEventListener('storage', handleStorage);
    clearInterval(interval);
  };
}, []);

  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab);
    const params = new URLSearchParams(location.search);
    params.set('tab', tab);
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  }, [location.pathname, location.search, navigate]);

  // --- Data ---
  const stats = useSystemStats();
  const pendingRequests = usePendingRequests();
  const allProjects = useAllProjects({});

  // pending updates از همه‌ی پروژه‌ها
  // 🆕 فیلتر محافظتی: هر پروژه‌ای که pendingVersionId دارد و قبلاً منتشر شده،
  // حتی اگر state اشتباه باشد، در تب بروزرسانی‌ها نشان داده می‌شود
  const pendingUpdates = useMemo(
    () => allProjects.filter((p) => {
      if (p.state === PROJECT_STATE.PENDING_INITIAL) return true;
      // حالت عادی
      if (p.state === PROJECT_STATE.UPDATE_PENDING) return true;
      // Fallback: اگر pendingVersionId دارد و از قبل منتشر شده
      if (p.pendingVersionId && p.publishedVersionId
          && p.state !== PROJECT_STATE.PUBLISHED
          && p.state !== PROJECT_STATE.SUSPENDED) {
        return true;
      }
      return false;
    }),
    [allProjects]
  );

  // --- Refresh ---
  const [refreshTick, setRefreshTick] = useState(0);
  const handleRefresh = useCallback(() => {
    setRefreshTick((t) => t + 1);
    toast?.('به‌روزرسانی شد', 'success');
  }, [toast]);

  // --- Shortcuts ---
  useKeyboardShortcuts({
    'ctrl+r': (e) => { e.preventDefault(); handleRefresh(); },
    'mod+r': (e) => { e.preventDefault(); handleRefresh(); },
  });

  if (initError) {
    return (
      <div className="ab-error-page">
        <EmptyState icon="⚠️" title="خطا در راه‌اندازی" description={String(initError?.message || initError)} />
      </div>
    );
  }

  if (!ready) return <AdminLoading isDarkMode={isDarkMode} />;

  return (
    <div className={`ab-page ${isDarkMode ? 'dark-mode' : ''}`}>
      <AdminBackground />

      <header className="ab-header">
        <div className="ab-header-inner">
          <div className="ab-header-id">
            <div className="ab-badge-icon">👑</div>
            <div>
              <h1 className="ab-title">پنل مدیریت کتاب‌ها</h1>
              <div className="ab-subtitle">
                مرکز کنترل درخواست‌ها و پروژه‌های معلمان
              </div>
            </div>
          </div>
          <div className="ab-header-actions">
            <button
              type="button"
              className="ab-theme-toggle"
              onClick={toggleTheme}
              title={isDarkMode ? 'حالت روشن' : 'حالت تیره'}
              aria-label={isDarkMode ? 'حالت روشن' : 'حالت تیره'}
            >
              {isDarkMode ? '☀️' : '🌙'}
            </button>
            {/* 🆕 دسترسی مستقیم به مدیریت دسته‌بندی‌ها */}
            <button
              type="button"
              className="ab-icon-btn"
              onClick={() => navigate('/admin/categories')}
              title="مدیریت دسته‌بندی‌ها"
              aria-label="مدیریت دسته‌بندی‌ها"
              style={{ fontSize: '1.1rem' }}
            >
              📂
            </button>
            <button className="ab-icon-btn" onClick={handleRefresh} title="به‌روزرسانی (Ctrl+R)">
              <IconRefresh />
            </button>
            <NotificationBell onNavigate={(link) => navigate(link)} />
          </div>
        </div>

        <div className="ab-stats-grid">
          <StatCard
            icon="⏳"
            value={stats.requests.pending}
            label="درخواست در انتظار"
            tone="warning"
            onClick={() => handleTabChange(TABS.REQUESTS)}
          />
          <StatCard
            icon="🔄"
            value={pendingUpdates.length}
            label="بروزرسانی در انتظار"
            tone="info"
            onClick={() => handleTabChange(TABS.UPDATES)}
          />
          <StatCard
            icon="✅"
            value={stats.projects.published}
            label="پروژه‌های منتشرشده"
            tone="success"
            onClick={() => handleTabChange(TABS.PROJECTS)}
          />
          <StatCard
            icon="👥"
            value={stats.teachers.total}
            label="معلمان"
            tone="purple"
          />
          <StatCard
            icon="📚"
            value={stats.projects.total}
            label="کل پروژه‌ها"
            tone="neutral"
            onClick={() => handleTabChange(TABS.PROJECTS)}
          />
          <StatCard
            icon="✏️"
            value={stats.requests.editRequested}
            label="درخواست اصلاح"
            tone="warning"
            onClick={() => handleTabChange(TABS.REQUESTS)}
          />
        </div>
      </header>

      <nav className="ab-tabs" role="tablist">
        {Object.entries(TAB_LABELS).map(([key, label]) => {
          const badge =
            key === TABS.REQUESTS ? pendingRequests.length :
            key === TABS.UPDATES ? pendingUpdates.length :
            key === TABS.PROJECTS ? allProjects.length : 0;
          return (
            <button
              key={key}
              role="tab"
              aria-selected={activeTab === key}
              className={`ab-tab ${activeTab === key ? 'active' : ''}`}
              onClick={() => handleTabChange(key)}
            >
              <span className="ab-tab-label">{label}</span>
              {badge > 0 && <span className="ab-tab-badge">{badge}</span>}
            </button>
          );
        })}
      </nav>

      <main className="ab-main">
        {activeTab === TABS.REQUESTS && (
          <RequestsQueueTab
            requests={pendingRequests}
            onOpenReview={(reqId) => navigate(`/admin/books/review/${reqId}`)}
          />
        )}
        {activeTab === TABS.UPDATES && (
          <UpdatesQueueTab
            updates={pendingUpdates}
            onOpenReview={(projectId) => navigate(`/admin/books/review/project/${projectId}`)}
          />
        )}
        {activeTab === TABS.PROJECTS && (
          <ProjectsTab projects={allProjects} />
        )}
        {activeTab === TABS.AUDIT && (
          <AuditLogTab refreshTick={refreshTick} />
        )}
      </main>
    </div>
  );
}

// ================================================================
// SECTION 5: BACKGROUND + LOADING
// ================================================================

function AdminBackground() {
  return (
    <div className="ab-bg" aria-hidden="true">
      <div className="ab-bg-orb ab-bg-orb-1" />
      <div className="ab-bg-orb ab-bg-orb-2" />
      <div className="ab-bg-orb ab-bg-orb-3" />
      <div className="ab-bg-grid" />
    </div>
  );
}

function AdminLoading({ isDarkMode }) {
  return (
    <div className={`ab-page ${isDarkMode ? 'dark-mode' : ''}`}>
      <AdminBackground />
      <header className="ab-header">
        <div className="ab-header-inner">
          <SkeletonLoader variant="line" />
        </div>
      </header>
      <main className="ab-main">
        <SkeletonLoader variant="card" count={4} />
      </main>
    </div>
  );
}

// ================================================================
// SECTION 6: STAT CARD
// ================================================================

function StatCard({ icon, value, label, tone = 'neutral', onClick }) {
  return (
    <button
      type="button"
      className={`ab-stat-card ab-stat-${tone}`}
      onClick={onClick}
      disabled={!onClick}
    >
      <div className="ab-stat-icon">{icon}</div>
      <div className="ab-stat-body">
        <div className="ab-stat-value">{value ?? 0}</div>
        <div className="ab-stat-label">{label}</div>
      </div>
    </button>
  );
}

// ================================================================
// SECTION 7: REQUESTS QUEUE TAB
// ================================================================

function RequestsQueueTab({ requests, onOpenReview }) {
  const [filter, setFilter] = useState('all'); // all | pending | edit-requested

  const filtered = useMemo(() => {
    if (filter === 'all') return requests;
    return requests.filter((r) => r.status === filter);
  }, [requests, filter]);

  if (requests.length === 0) {
    return (
      <div className="ab-tab-body">
        <EmptyState
          icon="🎉"
          title="هیچ درخواست در انتظاری نیست"
          description="همه‌ی درخواست‌ها بررسی شده‌اند."
        />
      </div>
    );
  }

  return (
    <div className="ab-tab-body">
      <div className="ab-tab-toolbar">
        <h2 className="ab-section-title">
          درخواست‌های معلمی در انتظار بررسی
        </h2>
        <div className="ab-filters">
          <FilterChip active={filter === 'all'} onClick={() => setFilter('all')}>
            همه ({requests.length})
          </FilterChip>
          <FilterChip
            active={filter === REQUEST_STATUS.PENDING}
            onClick={() => setFilter(REQUEST_STATUS.PENDING)}
          >
            در انتظار بررسی ({requests.filter((r) => r.status === REQUEST_STATUS.PENDING).length})
          </FilterChip>
          <FilterChip
            active={filter === REQUEST_STATUS.EDIT_REQUESTED}
            onClick={() => setFilter(REQUEST_STATUS.EDIT_REQUESTED)}
          >
            نیاز به اصلاح ({requests.filter((r) => r.status === REQUEST_STATUS.EDIT_REQUESTED).length})
          </FilterChip>
        </div>
      </div>

      <div className="ab-requests-grid">
        {filtered.map((r) => (
          <AdminRequestCard
            key={r.id}
            request={r}
            onOpen={() => onOpenReview(r.id)}
          />
        ))}
      </div>
    </div>
  );
}

function FilterChip({ active, onClick, children }) {
  return (
    <button
      type="button"
      className={`ab-filter-chip ${active ? 'active' : ''}`}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

// ================================================================
// SECTION 8: ADMIN REQUEST CARD
// ================================================================

function AdminRequestCard({ request, onOpen }) {
  const { approve, reject, requestEdit } = useAdminRequestActions();
  const [confirmType, setConfirmType] = useState(null); // 'approve' | 'reject' | 'edit'
  const [reason, setReason] = useState('');
  const [showDetails, setShowDetails] = useState(false);
  const [actionError, setActionError] = useState(null);   // 🆕

  const gradeLabel = GRADE_TITLES[String(request.grade)] || `پایه ${request.grade}`;
  const subjectLabel = SUBJECT_LABELS[request.subject] || request.subject;
  // 🔽 CHANGED
  const gradeFieldLabel = formatGradeField(request.grade, request.field);

  // دریافت معلم (اگه TeacherProfile داره)
  const teacherProfile = useMemo(() => {
    if (!request.userId) return null;
    return dataEngine.teacher.getByUserId(request.userId);
  }, [request.userId]);

  // پروژه‌های فعلی این معلم (اگه داره)
  const teacherProjects = useMemo(() => {
    if (!teacherProfile) return [];
    return dataEngine.project.listByTeacher(teacherProfile.id);
  }, [teacherProfile]);

  // 🆕 استخراج پیام خطای خوانا از نتیجه‌ی useAction
  const extractActionError = useCallback((res) => {
    const raw = (res?.errors || [])[0] || 'خطای نامشخص در انجام عملیات';
    const map = {
      project_exists: 'پروژه‌ی مشابه از قبل برای این معلم و پایه/کتاب وجود دارد. ابتدا پروژه‌ی قبلی را حذف کنید (از تب «همه‌ی پروژه‌ها» → «حذف کامل») و سپس دوباره تایید بزنید.',
      project_key_taken: 'پروژه‌ای با این ترکیب پایه/کتاب/پوشه قبلاً ثبت شده است.',
      not_found: 'درخواست یافت نشد.',
      forbidden: 'شما اجازه‌ی این عملیات را ندارید.',
      rate_limited: 'تعداد درخواست‌ها زیاد است. کمی بعد دوباره تلاش کنید.',
      invalid_grade_or_subject: 'پایه یا کتاب معتبر نیست.',
      field_required: 'انتخاب رشته برای پایه‌های ۱۰ تا ۱۲ الزامی است.',
    };
    return map[raw] || raw;
  }, []);

  const handleConfirm = useCallback(async () => {
    setActionError(null);
    if (confirmType === 'approve') {
      const res = await approve.run(request.id, reason);
      if (res?.ok) {
        setConfirmType(null);
        setReason('');
      } else {
        setActionError(extractActionError(res));
      }
    } else if (confirmType === 'reject') {
      const res = await reject.run(request.id, reason);
      if (res?.ok) {
        setConfirmType(null);
        setReason('');
      } else {
        setActionError(extractActionError(res));
      }
    } else if (confirmType === 'edit') {
      const res = await requestEdit.run(request.id, reason);
      if (res?.ok) {
        setConfirmType(null);
        setReason('');
      } else {
        setActionError(extractActionError(res));
      }
    }
  }, [confirmType, request.id, reason, approve, reject, requestEdit, extractActionError]);

  const isPending = request.status === REQUEST_STATUS.PENDING;
  const isEditRequested = request.status === REQUEST_STATUS.EDIT_REQUESTED;

  return (
    <article className={`ab-request-card status-${request.status}`}>
      <header className="ab-request-header">
        <div className="ab-request-avatar">
          {request.formData?.avatarBlobId ? (
            <BlobImage blobId={request.formData.avatarBlobId} alt={request.formData.fullName} />
          ) : (
            <span className="ab-avatar-fallback">
              {(request.formData?.fullName || '؟').charAt(0)}
            </span>
          )}
        </div>
        <div className="ab-request-title-group">
          <h3 className="ab-request-name">{request.formData?.fullName || 'بدون نام'}</h3>
          <div className="ab-request-target">
            <span className="ab-target-emoji">📚</span>
            {/* 🔽 CHANGED: نمایش رشته */}
            {subjectLabel} — {gradeFieldLabel}
          </div>
        </div>
        <Badge tone={isPending ? 'warning' : 'info'} size="sm">
          {isPending ? '⏳ در انتظار' : '✏️ نیاز به اصلاح'}
        </Badge>
      </header>

      <div className="ab-request-meta-grid">
        <MetaItem icon="🏷️" label="folderName" value={request.proposedFolderName} mono />
        <MetaItem icon="💼" label="سابقه" value={`${request.formData?.experienceYears || 0} سال`} />
        <MetaItem
          icon="📅"
          label="تاریخ ثبت"
          value={request.submittedAt ? new Date(request.submittedAt).toLocaleDateString('fa-IR') : '—'}
        />
        <MetaItem
          icon="⭐"
          label="تخصص‌ها"
          value={request.formData?.specialties?.join(' · ') || '—'}
        />
      </div>

      {request.formData?.bio && (
        <div className="ab-request-bio">
          <div className="ab-request-bio-label">📝 بیوگرافی</div>
          <p>{request.formData.bio}</p>
        </div>
      )}

      {request.adminNotes && (
        <div className="ab-request-note">
          <strong>یادداشت قبلی مدیر:</strong> {request.adminNotes}
        </div>
      )}

      {/* پروژه‌های قبلی این معلم */}
      {teacherProfile && teacherProjects.length > 0 && (
        <div className="ab-request-projects">
          <button
            className="ab-request-projects-toggle"
            onClick={() => setShowDetails((s) => !s)}
          >
            <span>📖 این معلم {teacherProjects.length} پروژه دارد</span>
            <IconArrow style={{
              transform: showDetails ? 'rotate(90deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s',
            }} />
          </button>
          {showDetails && (
            <div className="ab-request-projects-list">
              {teacherProjects.map((p) => (
                <div key={p.id} className="ab-request-project-item">
                  <ProjectStateBadge state={p.state} size="sm" />
                  <span className="ab-request-project-title">
                    {p.title || `${SUBJECT_LABELS[p.subject]} — ${GRADE_TITLES[String(p.grade)]}`}
                  </span>
                  <Link
                    to={`/admin/books/review/project/${p.id}`}
                    className="ab-link"
                    onClick={(e) => e.stopPropagation()}
                  >
                    مشاهده
                  </Link>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {!teacherProfile && (
        <div className="ab-request-note info">
          ✨ این معلم هنوز پروژه‌ای ندارد — با تایید، اولین پروژه‌اش ساخته می‌شود.
        </div>
      )}

      {/* 🆕 نمایش خطای انجام عملیات (در خود کارت) */}
      {actionError && (
        <div
          role="alert"
          style={{
            marginTop: 12,
            padding: '12px 14px',
            background: '#fee2e2',
            border: '1px solid #fca5a5',
            borderRadius: 10,
            color: '#991b1b',
            fontSize: '0.85rem',
            lineHeight: 1.7,
            display: 'flex',
            gap: 8,
            alignItems: 'flex-start',
          }}
        >
          <span style={{ fontSize: '1.1rem', lineHeight: 1 }}>⚠️</span>
          <div>
            <strong style={{ display: 'block', marginBottom: 4 }}>خطا در انجام عملیات</strong>
            <span>{actionError}</span>
          </div>
        </div>
      )}

      <footer className="ab-request-actions">
        <button
          className="ab-btn ab-btn-ghost"
          onClick={onOpen}
        >
          <IconEye size={14} /> بررسی عمیق
        </button>
        <div className="ab-btn-group">
          {(isPending || isEditRequested) && (
            <>
              <button
                className="ab-btn ab-btn-warning"
                onClick={() => { setConfirmType('edit'); setReason(''); setActionError(null); }}
              >
                <IconEdit size={14} /> درخواست اصلاح
              </button>
              <button
                className="ab-btn ab-btn-danger"
                onClick={() => { setConfirmType('reject'); setReason(''); setActionError(null); }}
              >
                <IconX size={14} /> رد
              </button>
              <button
                className="ab-btn ab-btn-success"
                onClick={() => { setConfirmType('approve'); setReason(''); setActionError(null); }}
              >
                <IconCheck size={14} /> تایید
              </button>
            </>
          )}
        </div>
      </footer>

      <ConfirmModal
        isOpen={confirmType === 'approve'}
        onClose={() => { setConfirmType(null); setActionError(null); }}
        onConfirm={handleConfirm}
        title="تایید درخواست معلمی"
        message={`آیا از تایید درخواست ${subjectLabel} ${gradeLabel} برای ${request.formData?.fullName} مطمئن هستید؟ پس از تایید، پروژه ساخته می‌شود.`}
        confirmText="✓ تایید کن"
        tone="info"
        requireReason
        reasonLabel="یادداشت (اختیاری)"
        loading={approve.loading}
        extraContent={actionError ? (
          <div
            role="alert"
            style={{
              marginTop: 10,
              padding: '10px 12px',
              background: '#fee2e2',
              border: '1px solid #fca5a5',
              borderRadius: 10,
              color: '#991b1b',
              fontSize: '0.85rem',
              lineHeight: 1.6,
            }}
          >
            ⚠️ {actionError}
          </div>
        ) : null}
      />

      <ConfirmModal
        isOpen={confirmType === 'reject'}
        onClose={() => { setConfirmType(null); setActionError(null); }}
        onConfirm={handleConfirm}
        title="رد درخواست معلمی"
        message="آیا از رد این درخواست مطمئن هستید؟ لطفاً دلیل را ذکر کنید تا معلم بتواند اصلاح کند."
        confirmText="✕ رد کن"
        tone="danger"
        requireReason
        reasonLabel="دلیل رد"
        reasonRequired
        loading={reject.loading}
        extraContent={actionError ? (
          <div
            role="alert"
            style={{
              marginTop: 10,
              padding: '10px 12px',
              background: '#fee2e2',
              border: '1px solid #fca5a5',
              borderRadius: 10,
              color: '#991b1b',
              fontSize: '0.85rem',
              lineHeight: 1.6,
            }}
          >
            ⚠️ {actionError}
          </div>
        ) : null}
      />

      <ConfirmModal
        isOpen={confirmType === 'edit'}
        onClose={() => { setConfirmType(null); setActionError(null); }}
        onConfirm={handleConfirm}
        title="درخواست اصلاح"
        message="به معلم اجازه می‌دهید درخواست را ویرایش کند؟ لطفاً توضیح دهید چه چیزی باید تغییر کند."
        confirmText="ارسال درخواست اصلاح"
        tone="warning"
        requireReason
        reasonLabel="چه چیزی باید اصلاح شود؟"
        reasonRequired
        loading={requestEdit.loading}
        extraContent={actionError ? (
          <div
            role="alert"
            style={{
              marginTop: 10,
              padding: '10px 12px',
              background: '#fee2e2',
              border: '1px solid #fca5a5',
              borderRadius: 10,
              color: '#991b1b',
              fontSize: '0.85rem',
              lineHeight: 1.6,
            }}
          >
            ⚠️ {actionError}
          </div>
        ) : null}
      />
    </article>
  );
}

function MetaItem({ icon, label, value, mono }) {
  return (
    <div className="ab-meta-item">
      <span className="ab-meta-icon">{icon}</span>
      <div className="ab-meta-body">
        <div className="ab-meta-label">{label}</div>
        <div className={`ab-meta-value ${mono ? 'mono' : ''}`}>{value}</div>
      </div>
    </div>
  );
}

// ================================================================
// SECTION 9: UPDATES QUEUE TAB
// ================================================================

function UpdatesQueueTab({ updates, onOpenReview }) {
  if (updates.length === 0) {
    return (
      <div className="ab-tab-body">
        <EmptyState
          icon="✨"
          title="هیچ بروزرسانی‌ای در انتظار نیست"
          description="همه‌ی نسخه‌های جدید بررسی شده‌اند."
        />
      </div>
    );
  }

  return (
    <div className="ab-tab-body">
      <div className="ab-tab-toolbar">
        <h2 className="ab-section-title">بروزرسانی‌های در انتظار بررسی</h2>
      </div>
      <div className="ab-updates-grid">
        {updates.map((p) => (
          <UpdateQueueCard key={p.id} project={p} onOpen={() => onOpenReview(p.id)} />
        ))}
      </div>
    </div>
  );
}

function UpdateQueueCard({ project, onOpen }) {
  const navigate = useNavigate();
  const teacherProfile = useTeacherProfile(project.teacherId);
  
  // 🆕 دریافت اطلاعات نسخه در انتظار برای خواندن یادداشت معلم
  const pendingVersion = useVersion(project.pendingVersionId);

  // diff بین published و pending
  const diff = useDiff(project.publishedVersionId, project.pendingVersionId);
  const impact = useImpactAnalysis(project.id, diff);

  const gradeLabel = GRADE_TITLES[String(project.grade)] || `پایه ${project.grade}`;
  const subjectLabel = SUBJECT_LABELS[project.subject] || project.subject;
  // 🔽 CHANGED
  const gradeFieldLabel = formatGradeField(project.grade, project.field);

  return (
    <article className="ab-update-card">
      <header className="ab-update-header">
        <div className="ab-update-teacher">
          {teacherProfile?.avatarBlobId ? (
            <BlobImage blobId={teacherProfile.avatarBlobId} alt={teacherProfile.fullName} />
          ) : teacherProfile?.legacyAvatar ? (
            <img src={teacherProfile.legacyAvatar} alt={teacherProfile.fullName} />
          ) : (
            <span className="ab-avatar-fallback">
              {(teacherProfile?.fullName || '؟').charAt(0)}
            </span>
          )}
          <div className="ab-update-teacher-info">
            <div className="ab-update-teacher-name">{teacherProfile?.fullName || 'معلم'}</div>
            <div className="ab-update-target">
              {/* 🔽 CHANGED: نمایش رشته */}
              {subjectLabel} — {gradeFieldLabel}
            </div>
          </div>
        </div>
        <Badge tone="warning" size="sm">🔄 بروزرسانی</Badge>
      </header>

      <div className="ab-update-body">
        <div className="ab-update-versions">
          <div className="ab-update-version-item">
            <span className="ab-update-version-label">نسخه‌ی فعال:</span>
            <VersionBadge status={VERSION_STATUS.PUBLISHED} size="sm" />
          </div>
          <div className="ab-update-arrow">→</div>
          <div className="ab-update-version-item">
            <span className="ab-update-version-label">نسخه‌ی جدید:</span>
            <VersionBadge status={VERSION_STATUS.PENDING} size="sm" />
          </div>
        </div>

        {diff && (
          <div className="ab-update-diff">
            <div className="ab-update-diff-title">خلاصه‌ی تغییرات:</div>
            <div className="ab-update-diff-stats">
              <span className="ab-update-diff-stat add">➕ {diff.summary.addedCount}</span>
              <span className="ab-update-diff-stat mod">✏️ {diff.summary.modifiedCount}</span>
              <span className="ab-update-diff-stat del">🗑 {diff.summary.removedCount}</span>
              <span className="ab-update-diff-stat mov">↔ {diff.summary.movedCount}</span>
            </div>
          </div>
        )}

        {impact?.data?.summary && impact.data.summary.totalStudents > 0 && (
          <div className="ab-update-impact">
            <div className="ab-update-impact-item">
              <span className="ab-update-impact-icon">👥</span>
              <span>{impact.data.summary.totalStudents} دانش‌آموز</span>
            </div>
            {impact.data.summary.studentsLosingProgress > 0 && (
              <div className="ab-update-impact-item warn">
                <span className="ab-update-impact-icon">⚠️</span>
                <span>{impact.data.summary.studentsLosingProgress} نفر با داده آرشیوی</span>
              </div>
            )}
          </div>
        )}

        {/* 🆕 نمایش یادداشت معلم در کارت */}
        {pendingVersion?.teacherNote && (
          <div className="ab-update-diff" style={{ background: '#f0f9ff', border: '1px solid #bae6fd' }}>
            <div className="ab-update-diff-title" style={{ color: '#0369a1' }}>📝 یادداشت معلم:</div>
            <p style={{ margin: 0, fontSize: '0.85rem', color: '#0c4a6e', lineHeight: 1.6 }}>
              {pendingVersion.teacherNote}
            </p>
          </div>
        )}

        <div className="ab-update-time">
          <IconClock size={14} />
          ثبت‌شده: {project.updatedAt ? new Date(project.updatedAt).toLocaleString('fa-IR') : '—'}
        </div>
      </div>

      <footer className="ab-update-actions">
        <button className="ab-btn ab-btn-primary" onClick={onOpen}>
          <IconEye size={14} /> بررسی و تصمیم
        </button>
      </footer>
    </article>
  );
}

// ================================================================
// SECTION 10: PROJECTS TAB
// ================================================================

function ProjectsTab({ projects }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [gradeFilter, setGradeFilter] = useState('');
  const [fieldFilter, setFieldFilter] = useState('');      // 🔽 CHANGED
  const [subjectFilter, setSubjectFilter] = useState('');
  const [stateFilter, setStateFilter] = useState('');

  const filtered = useMemo(() => {
    let list = projects;
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      list = list.filter((p) => {
        const tp = dataEngine.teacher.get(p.teacherId);
        return (
          (p.title || '').toLowerCase().includes(q) ||
          (tp?.fullName || '').toLowerCase().includes(q) ||
          (p.folderName || '').toLowerCase().includes(q)
        );
      });
    }
    if (gradeFilter) list = list.filter((p) => curriculumNormalizeGrade(p.grade) === gradeFilter);
    // 🔽 CHANGED: فیلتر رشته
    if (fieldFilter) {
      const nf = curriculumNormalizeField(fieldFilter);
      list = list.filter((p) => (p.field || null) === nf);
    }
    if (subjectFilter) list = list.filter((p) => p.subject === subjectFilter);
    if (stateFilter) list = list.filter((p) => p.state === stateFilter);
    return list;
  }, [projects, query, gradeFilter, fieldFilter, subjectFilter, stateFilter]);

  const subjectOptions = useMemo(() => {
    const set = new Set(projects.map((p) => p.subject));
    return Array.from(set);
  }, [projects]);

  return (
    <div className="ab-tab-body">
      <div className="ab-tab-toolbar">
        <h2 className="ab-section-title">همه‌ی پروژه‌ها ({projects.length})</h2>
      </div>

      <div className="ab-projects-toolbar">
        <div className="ab-search-box">
          <IconSearch />
          <input
            type="text"
            className="ab-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="جستجوی نام معلم، عنوان یا شناسه پوشه..."
          />
          {query && (
            <button className="ab-search-clear" onClick={() => setQuery('')}>
              <IconX size={14} />
            </button>
          )}
        </div>

        {/* 🔽 CHANGED: فیلتر پایه — همه‌ی ۱۲ پایه */}
        <select className="ab-select" value={gradeFilter} onChange={(e) => {
          setGradeFilter(e.target.value);
          // 🔽 CHANGED: ریست رشته اگه پایه جدید نیازی نداره
          if (!curriculumNeedsField(e.target.value)) setFieldFilter('');
        }}>
          <option value="">همه‌ی پایه‌ها</option>
          {GRADE_LIST.map((g) => (
            <option key={g.id} value={g.id}>{g.title}</option>
          ))}
        </select>

        {/* 🔽 CHANGED: فیلتر رشته — فقط وقتی پایه ۱۰-۱۲ انتخاب شده */}
        {curriculumNeedsField(gradeFilter) && (
          <select className="ab-select" value={fieldFilter} onChange={(e) => setFieldFilter(e.target.value)}>
            <option value="">همه‌ی رشته‌ها</option>
            {FIELD_LIST.map((f) => (
              <option key={f.id} value={f.id}>{f.title}</option>
            ))}
          </select>
        )}

        <select className="ab-select" value={subjectFilter} onChange={(e) => setSubjectFilter(e.target.value)}>
          <option value="">همه‌ی کتاب‌ها</option>
          {subjectOptions.map((s) => (
            <option key={s} value={s}>{SUBJECT_LABELS[s] || s}</option>
          ))}
        </select>

        <select className="ab-select" value={stateFilter} onChange={(e) => setStateFilter(e.target.value)}>
          <option value="">همه‌ی وضعیت‌ها</option>
          <option value={PROJECT_STATE.BUILDING}>در حال ساخت</option>
          <option value={PROJECT_STATE.PENDING_INITIAL}>در انتظار بررسی اولیه</option>
          <option value={PROJECT_STATE.PUBLISHED}>منتشرشده</option>
          <option value={PROJECT_STATE.UPDATE_PENDING}>در انتظار بروزرسانی</option>
          <option value={PROJECT_STATE.SUSPENDED}>معلق</option>
        </select>
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          icon="🔍"
          title="پروژه‌ای یافت نشد"
          description={projects.length === 0 ? 'هنوز پروژه‌ای ساخته نشده.' : 'فیلترها را تغییر دهید.'}
        />
      ) : (
        <div className="ab-projects-list">
          {filtered.map((p) => (
            <AdminProjectRow key={p.id} project={p} />
          ))}
        </div>
      )}
    </div>
  );
}

function AdminProjectRow({ project }) {
  const navigate = useNavigate();
  const teacherProfile = useTeacherProfile(project.teacherId);
  const gradeLabel = GRADE_TITLES[String(project.grade)] || `پایه ${project.grade}`;
  const subjectLabel = SUBJECT_LABELS[project.subject] || project.subject;

  // 🔽 CHANGED: نمایش کامل با رشته
  const gradeFieldLabel = formatGradeField(project.grade, project.field);
  const displayTitle = project.title || `${subjectLabel} — ${gradeFieldLabel}`;

  return (
    <article className="ab-project-row">
      <div className="ab-project-row-cover">
        {project.coverImageBlobId ? (
          <BlobImage blobId={project.coverImageBlobId} alt={project.title} />
        ) : (
          <div className={`ab-project-row-placeholder g-${project.grade}`}>📘</div>
        )}
      </div>
      <div className="ab-project-row-body">
        <div className="ab-project-row-title">
          {displayTitle}
        </div>
        <div className="ab-project-row-meta">
          <span>👤 {teacherProfile?.fullName || 'معلم'}</span>
          <span>🏷️ {project.folderName}</span>
          <span>📅 {new Date(project.createdAt).toLocaleDateString('fa-IR')}</span>
          {/* 🆕 نمایش وضعیت اشتراک */}
          {project.subscriptionEnabled && (
            <span style={{ color: '#6C5CE7', fontWeight: 800 }}>
              💰 {Number(project.subscriptionPrice).toLocaleString('fa-IR')} تومان
            </span>
          )}
        </div>
      </div>
      <div className="ab-project-row-state">
        <ProjectStateBadge state={project.state} size="sm" />
      </div>
      <div className="ab-project-row-actions">
        <button
          className="ab-btn ab-btn-ghost ab-btn-sm"
          onClick={() => navigate(`/admin/books/review/project/${project.id}`)}
        >
          <IconEye size={14} /> مشاهده
        </button>
      </div>
    </article>
  );
}

// ================================================================
// SECTION 11: AUDIT LOG TAB
// ================================================================

function AuditLogTab({ refreshTick }) {
  const [filter, setFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [limit, setLimit] = useState(100);

  const entries = useAuditLog({ action: actionFilter || null, limit });

  // فراخوانی refreshTick برای re-render
  useEffect(() => { /* noop */ }, [refreshTick]);

  if (entries.length === 0) {
    return (
      <div className="ab-tab-body">
        <div className="ab-tab-toolbar">
          <h2 className="ab-section-title">تاریخچه‌ی اقدامات</h2>
        </div>
        <EmptyState
          icon="📋"
          title="هیچ رکوردی یافت نشد"
          description="اقدامات مدیر و معلمان اینجا نمایش داده می‌شود."
        />
      </div>
    );
  }

  const ACTION_LABELS = {
    [AUDIT_ACTION.REQUEST_CREATE]:       'ایجاد درخواست',
    [AUDIT_ACTION.REQUEST_SUBMIT]:       'ارسال درخواست',
    [AUDIT_ACTION.REQUEST_APPROVE]:      'تایید درخواست',
    [AUDIT_ACTION.REQUEST_REJECT]:       'رد درخواست',
    [AUDIT_ACTION.REQUEST_EDIT_REQUEST]: 'درخواست اصلاح',
    [AUDIT_ACTION.REQUEST_WITHDRAW]:     'پس گرفتن درخواست',
    [AUDIT_ACTION.REQUEST_EDIT]:         'ویرایش درخواست',
    [AUDIT_ACTION.TEACHER_CREATE]:       'ایجاد معلم',
    [AUDIT_ACTION.TEACHER_UPDATE]:       'ویرایش معلم',
    [AUDIT_ACTION.PROJECT_CREATE]:       'ایجاد پروژه',
    [AUDIT_ACTION.PROJECT_UPDATE]:       'ویرایش پروژه',
    [AUDIT_ACTION.PROJECT_PUBLISH]:      'انتشار پروژه',
    [AUDIT_ACTION.PROJECT_SUSPEND]:      'تعلیق پروژه',
    [AUDIT_ACTION.PROJECT_RESUME]:       'فعال‌سازی پروژه',
    [AUDIT_ACTION.VERSION_CREATE]:       'ایجاد نسخه',
    [AUDIT_ACTION.VERSION_UPDATE]:       'ویرایش نسخه',
    [AUDIT_ACTION.VERSION_SUBMIT]:       'ارسال نسخه',
    [AUDIT_ACTION.VERSION_APPROVE]:      'تایید و انتشار',
    [AUDIT_ACTION.VERSION_REJECT]:       'رد نسخه',
    [AUDIT_ACTION.VERSION_EDIT_REQUEST]: 'درخواست اصلاح نسخه',
    [AUDIT_ACTION.VERSION_ROLLBACK]:     'بازگردانی نسخه',
    [AUDIT_ACTION.PROGRESS_MIGRATE]:     'مهاجرت پیشرفت',
  };

  return (
    <div className="ab-tab-body">
      <div className="ab-tab-toolbar">
        <h2 className="ab-section-title">تاریخچه‌ی اقدامات</h2>
        <div className="ab-audit-filters">
          <select className="ab-select ab-select-sm" value={actionFilter} onChange={(e) => setActionFilter(e.target.value)}>
            <option value="">همه‌ی اقدامات</option>
            {Object.values(AUDIT_ACTION).map((a) => (
              <option key={a} value={a}>{ACTION_LABELS[a] || a}</option>
            ))}
          </select>
          <select className="ab-select ab-select-sm" value={limit} onChange={(e) => setLimit(Number(e.target.value))}>
            <option value={50}>۵۰ تای آخر</option>
            <option value={100}>۱۰۰ تای آخر</option>
            <option value={200}>۲۰۰ تای آخر</option>
            <option value={500}>۵۰۰ تای آخر</option>
          </select>
        </div>
      </div>

      <div className="ab-audit-list">
        {entries.map((e) => {
          const tp = e.actorId ? dataEngine.teacher.getByUserId(e.actorId) : null;
          return (
            <div key={e.id} className="ab-audit-row">
              <div className="ab-audit-time">
                {new Date(e.at).toLocaleString('fa-IR')}
              </div>
              <div className="ab-audit-action">
                <Badge tone={toneForAction(e.action)} size="sm">
                  {ACTION_LABELS[e.action] || e.action}
                </Badge>
              </div>
              <div className="ab-audit-actor">
                <span className="ab-audit-actor-role">[{e.actorRole || '?'}]</span>
                {' '}
                <span className="ab-audit-actor-name">
                  {tp?.fullName || e.actorId || '—'}
                </span>
              </div>
              <div className="ab-audit-target">
                {e.targetType && (
                  <span className="ab-audit-target-type">
                    {e.targetType}:
                  </span>
                )}
                <span className="ab-audit-target-id mono">
                  {(e.targetId || '').slice(0, 16)}
                </span>
              </div>
              {e.note && (
                <div className="ab-audit-note" title={e.note}>
                  {e.note.length > 60 ? `${e.note.slice(0, 60)}…` : e.note}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function toneForAction(action) {
  if (action.includes('approve') || action.includes('publish') || action.includes('resume')) return 'success';
  if (action.includes('reject') || action.includes('suspend') || action.includes('delete')) return 'danger';
  if (action.includes('edit_request') || action.includes('warning')) return 'warning';
  if (action.includes('create') || action.includes('update')) return 'info';
  return 'neutral';
}